/**
 * Search, filter, sort and paging of the lock explorer. Pure: the API route and the tests use the same functions, and the explorer's URL
 * (?q=&kind=&status=&sort=&page=) round-trips through `queryFromParams` / `queryToParams`.
 */
import { PAGE_SIZE, LockType } from '../config'
import type { KindFilter, LockQuery, LockSummary, SortKey, StatusFilter } from '../types'
import { isAddr, lc } from '../format'

export type ParsedQuery =
  | { mode: 'empty' }
  | { mode: 'address'; address: string }
  | { mode: 'prefix'; prefix: string }
  | { mode: 'id'; id: number; type?: LockType }
  | { mode: 'text'; text: string }

/** What the search box was given: a full address (token, pair, pool or owner), the start of one, a lock number ("17", "#17", "v3 9") or words. */
export function parseLockQuery(raw: string): ParsedQuery {
  const q = (raw ?? '').trim()
  if (!q) return { mode: 'empty' }
  if (isAddr(q)) return { mode: 'address', address: lc(q) }
  if (/^0x[0-9a-fA-F]{3,39}$/.test(q)) return { mode: 'prefix', prefix: lc(q) }
  const id = q.match(/^(?:(v2|v3)[\s#:_-]*|#\s*)?(\d{1,7})$/i)
  if (id) return { mode: 'id', id: Number(id[2]), type: id[1] ? (id[1].toLowerCase() as LockType) : undefined }
  return { mode: 'text', text: q.toLowerCase().slice(0, 60) }
}

const addressesOf = (l: LockSummary): string[] => [
  l.owner,
  l.contract.toLowerCase(),
  ...l.tokens.map((t) => t.address),
  l.pair?.pair ?? '',
  l.position?.pool ?? '',
  l.position?.manager ?? '',
]

export function matchesQuery(l: LockSummary, p: ParsedQuery): boolean {
  switch (p.mode) {
    case 'empty':
      return true
    case 'address':
      return addressesOf(l).includes(p.address)
    case 'prefix':
      return addressesOf(l).some((a) => a.startsWith(p.prefix))
    case 'id':
      return (!p.type || p.type === l.type) && (l.id === p.id || l.position?.nftId === String(p.id))
    default: {
      const hay = [l.title, l.description, ...l.tokens.map((t) => `${t.symbol} ${t.name}`)].join(' ').toLowerCase()
      return p.text.split(/\s+/).every((w) => hay.includes(w))
    }
  }
}

export const matchesKind = (l: LockSummary, k: KindFilter) => k === 'all' || l.kind === k
export function matchesStatus(l: LockSummary, s: StatusFilter) {
  switch (s) {
    case 'active':
      return l.phase === 'locked' || l.phase === 'vesting'
    case 'soon':
      return l.soon
    case 'due':
      return l.phase === 'due'
    case 'released':
      return l.phase === 'released'
    default:
      return true
  }
}

const rank = (l: LockSummary) => (l.phase === 'locked' || l.phase === 'vesting' ? 0 : l.phase === 'due' ? 1 : 2)
const byValue = (a: LockSummary, b: LockSummary) => {
  const av = a.valueUsd ?? -1
  const bv = b.valueUsd ?? -1
  return bv - av || b.lockedAt - a.lockedAt
}

/** value: most valuable first (unpriced last); unlock: the next release first, then locks that are due, then released ones; newest: latest lock first */
export function sortLocks(items: LockSummary[], sort: SortKey): LockSummary[] {
  const out = items.slice()
  if (sort === 'value') return out.sort((a, b) => rank(a) - rank(b) || byValue(a, b))
  if (sort === 'unlock') {
    return out.sort((a, b) => {
      const r = rank(a) - rank(b)
      if (r) return r
      if (rank(a) === 0) return (a.nextAt ?? a.unlockAt) - (b.nextAt ?? b.unlockAt) || a.id - b.id
      return b.unlockAt - a.unlockAt || b.id - a.id
    })
  }
  return out.sort((a, b) => b.lockedAt - a.lockedAt || b.id - a.id)
}

export function filterLocks(items: LockSummary[], query: Pick<LockQuery, 'q' | 'kind' | 'status' | 'owner' | 'token'>): LockSummary[] {
  const parsed = parseLockQuery(query.q)
  const owner = query.owner && isAddr(query.owner) ? lc(query.owner) : null
  const token = query.token && isAddr(query.token) ? lc(query.token) : null
  return items.filter(
    (l) =>
      matchesStatus(l, query.status) &&
      matchesKind(l, query.kind) &&
      matchesQuery(l, parsed) &&
      (!owner || l.owner === owner) &&
      (!token || addressesOf(l).includes(token)),
  )
}

export function paginate<T>(items: T[], page: number, pageSize = PAGE_SIZE) {
  const pages = Math.max(1, Math.ceil(items.length / pageSize))
  const p = Math.min(Math.max(1, Math.floor(page) || 1), pages)
  return { items: items.slice((p - 1) * pageSize, p * pageSize), page: p, pages, total: items.length, pageSize }
}

// ---- URL <-> query ---------------------------------------------------------------------------------------------------------------------

const KINDS: KindFilter[] = ['all', 'v3-position', 'lp-v2', 'token']
const STATUSES: StatusFilter[] = ['active', 'soon', 'due', 'released', 'all']
const SORTS: SortKey[] = ['value', 'unlock', 'newest']
export const DEFAULT_QUERY: LockQuery = { q: '', kind: 'all', status: 'active', sort: 'value', page: 1 }

type Params = Record<string, string | string[] | undefined>
const one = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v)

export function queryFromParams(params: Params): LockQuery {
  const kind = one(params.kind) as KindFilter
  const status = one(params.status) as StatusFilter
  const sort = one(params.sort) as SortKey
  const page = parseInt(one(params.page) ?? '1', 10)
  const owner = one(params.owner)
  const token = one(params.token)
  return {
    q: (one(params.q) ?? '').slice(0, 80),
    kind: KINDS.includes(kind) ? kind : DEFAULT_QUERY.kind,
    status: STATUSES.includes(status) ? status : DEFAULT_QUERY.status,
    sort: SORTS.includes(sort) ? sort : DEFAULT_QUERY.sort,
    page: Number.isFinite(page) && page > 0 ? Math.min(page, 10000) : 1,
    owner: owner && isAddr(owner) ? lc(owner) : undefined,
    token: token && isAddr(token) ? lc(token) : undefined,
  }
}

/** Only what differs from the defaults goes into the URL. */
export function queryToParams(q: LockQuery): Record<string, string> {
  const out: Record<string, string> = {}
  if (q.q) out.q = q.q
  if (q.kind !== DEFAULT_QUERY.kind) out.kind = q.kind
  if (q.status !== DEFAULT_QUERY.status) out.status = q.status
  if (q.sort !== DEFAULT_QUERY.sort) out.sort = q.sort
  if (q.page > 1) out.page = String(q.page)
  if (q.owner) out.owner = q.owner
  if (q.token) out.token = q.token
  return out
}

/** The explorer's live example: the active lock with the most value locked (unpriced ones only when nothing is priced). */
export function pickFeatured(items: LockSummary[]): LockSummary | null {
  const active = items.filter((l) => l.phase === 'locked' || l.phase === 'vesting')
  if (!active.length) return null
  return active.slice().sort((a, b) => (b.lockedUsd ?? -1) - (a.lockedUsd ?? -1) || a.id - b.id)[0]
}
